import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import Avatar from '../components/Avatar';
import './Chat.css';

interface Message {
  id: number;
  content: string;
  senderId: number;
  sender: {
    id: number;
    name?: string;
  };
  createdAt: string;
}

interface Match {
  id: number;
  matchedUser: {
    id: number;
    name?: string;
    profile?: {
      variant?: {
        name: string;
      };
      photoUrl?: string;
    };
  };
}

const Chat: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const { connected, joinMatch, sendMessage, onNewMessage, startTyping, stopTyping, onTyping, onStopTyping, markAsRead } = useSocket();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (matchId) {
      fetchMatch();
      fetchMessages();

      // Join WebSocket room for this match
      if (connected) {
        joinMatch(parseInt(matchId));
        markAsRead(parseInt(matchId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, connected]);

  useEffect(() => {
    // Listen for new messages
    onNewMessage((message: any) => {
      if (message.matchId === parseInt(matchId!)) {
        setMessages((prev) => [...prev, message]);
        markAsRead(parseInt(matchId!));
      }
    });

    // Listen for typing indicators
    onTyping((data) => {
      if (data.matchId === parseInt(matchId!) && data.userId !== user?.id) {
        setIsTyping(true);
      }
    });

    onStopTyping((data) => {
      if (data.matchId === parseInt(matchId!) && data.userId !== user?.id) {
        setIsTyping(false);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Polling for new messages (fallback when Socket.IO is not available)
  useEffect(() => {
    if (!matchId) return;

    // Poll every 5 seconds for new messages
    const pollInterval = setInterval(async () => {
      try {
        const response = await ApiService.get(`/messages/${matchId}`);
        const newMessages = response.data;

        // Only update if we have new messages
        if (newMessages.length > messages.length) {
          setMessages(newMessages);
          // Mark new messages as read
          if (connected) {
            markAsRead(parseInt(matchId));
          } else {
            // Mark as read via HTTP API if Socket.IO not connected
            try {
              await ApiService.put(`/messages/${matchId}/read`);
            } catch (error) {
              console.error('Failed to mark messages as read:', error);
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll messages:', error);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [matchId, messages.length, connected, markAsRead]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMatch = async () => {
    try {
      const response = await ApiService.get(`/matches/${matchId}`);
      setMatch(response.data);
    } catch (error) {
      console.error('Failed to fetch match:', error);
      navigate('/matches');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await ApiService.get(`/messages/${matchId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || !matchId) return;

    // Send via WebSocket if connected, fallback to API
    if (connected) {
      sendMessage(parseInt(matchId), newMessage.trim());
      setNewMessage('');
      stopTyping(parseInt(matchId));
    } else {
      // Fallback to HTTP API
      try {
        const response = await ApiService.post(`/messages/${matchId}`, {
          content: newMessage.trim(),
        });

        setMessages([...messages, response.data]);
        setNewMessage('');
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  };

  const handleTyping = (value: string) => {
    setNewMessage(value);

    if (!matchId) return;

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Start typing
    if (value.length > 0 && connected) {
      startTyping(parseInt(matchId));

      // Stop typing after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(parseInt(matchId));
      }, 2000);
    } else if (value.length === 0 && connected) {
      stopTyping(parseInt(matchId));
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="chat-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="chat-container">
        {match && (
          <>
            <div className="chat-header">
              <button onClick={() => navigate('/matches')} className="back-button">
                ← Back
              </button>
              <Avatar
                photoUrl={match.matchedUser.profile?.photoUrl}
                name={match.matchedUser.name}
                size="small"
              />
              <div className="chat-user-info">
                <h2>{match.matchedUser.name || 'Anonymous'}</h2>
                {match.matchedUser.profile?.variant && (
                  <span className="variant-badge">
                    {match.matchedUser.profile.variant.name}
                  </span>
                )}
              </div>
            </div>

            <div className="messages-container">
              {messages.length === 0 ? (
                <p className="no-messages-hint">
                  No messages yet. Say hi to start the conversation!
                </p>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message ${
                      message.senderId === user?.id ? 'sent' : 'received'
                    }`}
                  >
                    <div className="message-content">{message.content}</div>
                    <div className="message-time">
                      {new Date(message.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                ))
              )}
              {isTyping && (
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                  <p>{match?.matchedUser.name || 'User'} is typing...</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="message-form">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => handleTyping(e.target.value)}
                placeholder="Type a message..."
                className="message-input"
              />
              <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                Send
              </button>
              {connected ? (
                <span className="connection-status connected">● Real-time</span>
              ) : (
                <span className="connection-status polling">● Auto-refresh (5s)</span>
              )}
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default Chat;
